import React, { useRef, useState, useEffect } from "react";
import Input from "../components/Input";
import { Form } from "@unform/web";
import NotificationAlert from "react-notification-alert";
import {
    Button,
    Card,
    CardHeader,
    CardBody,
    FormGroup,
    Row,
    Col,
} from "reactstrap";
import { Link } from "react-router-dom";
import SweetAlert from "react-bootstrap-sweetalert";
import api from "../../services/api";
import CardSettings from "components/Cards/CardSettings";

import { useRestaurants } from "context/RestaurantsContext";
import { useAuth } from "context/AuthContext";
import { BANK_MANAGEMENT_USERS } from "services/consts";
import { ChatProviderCard } from "views/components/ChatProviderCard/ChatProviderCard";
import apiGd from "services/apiGd";

const updateChatProvider = async (provider, toast, token) => {
    try {
        const { data } = await apiGd.put(
            "/admin/settings/chat",
            {
                provider,
            },
            {
                headers: {
                    Authorization: `Bearer: ${token}`,
                },
            }
        );

        toast(
            "tr",
            "success",
            `Atualizado o provedor para ${data.toUpperCase()}`
        );
    } catch (err) {
        toast(
            "tr",
            "danger",
            err?.response?.data?.message || "Erro ao atualizar."
        );
    }
};

const getRestaurantInfo = async (
    setMenuEnable,
    setSystemEnable,
    setChatProvider
) => {
    const tokenGd = localStorage.getItem("@QRExpress:tokenGd");
    const response = await apiGd.get("/admin/settings", {
        headers: { Authorization: `Bearer ${tokenGd}` },
    });

    const clients = response.data.filter(
        item => item.name === "enable-clients"
    )[0];
    const menu = response.data.filter(item => item.name === "enable-menu")[0];

    setMenuEnable(menu?.value);
    setSystemEnable(clients?.value);

    const chatSettings = response.data?.find(
        setting => setting.name === "chat"
    );
    setChatProvider(chatSettings?.value || null);
};

function Settings() {
    const notificationAlert = useRef(null);
    const [chatProvider, setChatProvider] = useState(null);
    const [menuEnable, setMenuEnable] = useState(true);
    const [systemEnable, setSystemEnable] = useState(true);
    const [pixCreationBlocked, setPixCreationBlocked] = useState(false);
    const [pixAlert, setPixAlert] = useState(null);
    const { validateUser } = useRestaurants();
    const { user } = useAuth();

    const notify = (place, color, message) => {
        var options = {};
        options = {
            place: place,
            message: (
                <div>
                    <div>{message}</div>
                </div>
            ),
            type: color,
            icon: "now-ui-icons ui-1_bell-53",
            autoDismiss: 7,
        };
        notificationAlert.current.notificationAlert(options);
    };

    const formRef = useRef(null);
    const token = localStorage.getItem("@QRExpress:token");
    const [initialData, setInitialData] = useState([]);

    async function getSettings() {
        try {
            const response = await api.get("admin/settings", {
                headers: { Authorization: `Bearer ${token}` },
            });
            let { tax_multiplier, sms_service_price } = response.data[0];
            tax_multiplier = tax_multiplier * 100;
            setInitialData({ tax_multiplier, sms_service_price });
        } catch (err) {
            console.log("getSettings error:", err);
            alert("Erro ao carregar informações");
        }
    }

    async function updateSettings(data) {
        try {
            const update = {
                sms_service_price: data.sms_service_price,
                tax_multiplier: data.tax_multiplier / 100,
            };
            await api.put("admin/settings", update, {
                headers: { Authorization: `Bearer ${token}` },
            });
            notify("tr", "success", "Atualizado com sucesso!");
            getSettings();
        } catch (err) {
            notify(
                "tr",
                "danger",
                err?.response?.data?.message || "Erro ao atualizar."
            );
        }
    }

    const handleUpdate = provider => {
        updateChatProvider(provider, notify, token);
    };

    async function getPixCreationBlock() {
        try {
            const tokenGd = localStorage.getItem("@QRExpress:tokenGd");
            const { data } = await apiGd.get(
                "/admin/settings/pix-creation-block",
                {
                    headers: { Authorization: `Bearer ${tokenGd}` },
                }
            );
            setPixCreationBlocked(data.blocked);
        } catch (err) {
            console.log("getPixCreationBlock error:", err);
        }
    }

    function requestTogglePixCreationBlock(blocked) {
        setPixAlert(
            <SweetAlert
                warning
                showCancel
                style={{ display: "block", marginTop: "-100px" }}
                title="Tem certeza?"
                confirmBtnText={blocked ? "Sim, bloquear" : "Sim, liberar"}
                cancelBtnText="Cancelar"
                confirmBtnBsStyle={blocked ? "danger" : "success"}
                cancelBtnBsStyle="default"
                onConfirm={() => {
                    setPixAlert(null);
                    handleTogglePixCreationBlock(blocked);
                }}
                onCancel={() => setPixAlert(null)}
            >
                {blocked
                    ? "Isso vai BLOQUEAR a criação de Pix para TODOS os restaurantes."
                    : "Isso vai LIBERAR a criação de Pix para todos os restaurantes."}
            </SweetAlert>
        );
    }

    async function handleTogglePixCreationBlock(blocked) {
        try {
            const tokenGd = localStorage.getItem("@QRExpress:tokenGd");
            const { data } = await apiGd.put(
                "/admin/settings/pix-creation-block",
                { blocked },
                {
                    headers: { Authorization: `Bearer ${tokenGd}` },
                }
            );
            setPixCreationBlocked(data.blocked);
            notify(
                "tr",
                "success",
                data.blocked
                    ? "Criação de Pix bloqueada para todos os restaurantes."
                    : "Criação de Pix liberada para todos os restaurantes."
            );
        } catch (err) {
            notify(
                "tr",
                "danger",
                err?.response?.data?.message || "Erro ao atualizar."
            );
        }
    }

    useEffect(() => {
        validateUser();
        getSettings();
        getRestaurantInfo(setMenuEnable, setSystemEnable, setChatProvider);
        if (BANK_MANAGEMENT_USERS.includes(user?.user_id)) {
            getPixCreationBlock();
        }
    }, [setMenuEnable, setSystemEnable, setChatProvider, user]);

    return (
        <>
            <NotificationAlert ref={notificationAlert} />
            {pixAlert}
            <div className="content">
                <Form
                    ref={formRef}
                    initialData={initialData}
                    onSubmit={updateSettings}
                >
                    <Row>
                        <Col md="12">
                            <Card>
                                <CardHeader>
                                    <h5 className="title">Configurações</h5>
                                </CardHeader>
                                <CardBody>
                                    <Row>
                                        <Col md="6">
                                            <label>Taxa de SMS (R$)</label>
                                            <Input
                                                name="sms_service_price"
                                                type="text"
                                                placeholder="R$"
                                            />
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <label>
                                                    Taxa Paga pelo
                                                    Estabelecimento (%)
                                                </label>
                                                <Input
                                                    name="tax_multiplier"
                                                    type="text"
                                                    placeholder="%"
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <Row style={{ paddingTop: 15 }}>
                                        <Col md="3" className="ml-auto">
                                            <Button
                                                color="success"
                                                type="submit"
                                            >
                                                Atualizar Perfil
                                            </Button>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Form>
                <Row>
                    <Col md="4">
                        <CardSettings
                            menuEnable={menuEnable}
                            systemEnable={systemEnable}
                            setMenuEnable={setMenuEnable}
                            setSystemEnable={setSystemEnable}
                        />
                    </Col>
                    <Col md="4">
                        <ChatProviderCard
                            chatProvider={chatProvider}
                            setChatProvider={setChatProvider}
                            handleUpdate={handleUpdate}
                        />
                    </Col>
                </Row>
                {BANK_MANAGEMENT_USERS.includes(user?.user_id) && (
                    <Row style={{ paddingTop: 15 }}>
                        <Col md="12">
                            <Card>
                                <CardBody>
                                    <Link to="/gd/banks">
                                        <Button color="primary">
                                            Gerenciar Bancos
                                        </Button>
                                    </Link>
                                    <FormGroup
                                        check
                                        style={{ paddingTop: 15 }}
                                    >
                                        <label className="form-check-label">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={pixCreationBlocked}
                                                onChange={e =>
                                                    requestTogglePixCreationBlock(
                                                        e.target.checked
                                                    )
                                                }
                                            />
                                            Bloquear criação de Pix para todos os
                                            restaurantes
                                            <span className="form-check-sign">
                                                <span className="check" />
                                            </span>
                                        </label>
                                    </FormGroup>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                )}
            </div>
        </>
    );
}

export default Settings;
